'use client';

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { settlementApi } from '../../services/settlementApi';
import { ArrowLeft, CheckCircle, CreditCard, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    draft: { label: 'Taslak', color: 'bg-gray-100 text-gray-700' },
    confirmed: { label: 'Onaylandı', color: 'bg-blue-100 text-blue-700' },
    paid: { label: 'Ödendi', color: 'bg-green-100 text-green-700' },
    disputed: { label: 'İtiraz', color: 'bg-red-100 text-red-700' },
};

export default function SettlementDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [settlement, setSettlement] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    // Payment modal
    const [showPayModal, setShowPayModal] = useState(false);
    const [payForm, setPayForm] = useState({ paidAmount: 0, paymentNote: '', paymentMethod: 'bank_transfer' });
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchSettlement();
    }, [id]);

    const fetchSettlement = async () => {
        try {
            setLoading(true);
            const data = await settlementApi.getById(id!);
            setSettlement(data);
            setPayForm({ ...payForm, paidAmount: data.remainingAmount || data.vendorShare || 0 });
        } catch {
            toast.error('Mutabakat yüklenemedi');
            navigate('/admin/settlements');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        try {
            setProcessing(true);
            await settlementApi.confirm(id!);
            toast.success('Mutabakat onaylandı');
            fetchSettlement();
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Onaylama başarısız');
        } finally {
            setProcessing(false);
        }
    };

    const handlePay = async () => {
        if (payForm.paidAmount <= 0) {
            toast.error('Geçerli bir tutar girin');
            return;
        }
        try {
            setProcessing(true);
            await settlementApi.markPaid(id!, payForm);
            toast.success('Ödeme kaydedildi');
            setShowPayModal(false);
            fetchSettlement();
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Ödeme kaydedilemedi');
        } finally {
            setProcessing(false);
        }
    };

    const handleRecalculate = async () => {
        try {
            setProcessing(true);
            const result = await settlementApi.recalculate(id!);
            toast.success('Yeniden hesaplandı');
            navigate(`/admin/settlements/${result.id}`);
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Hesaplama başarısız');
        } finally {
            setProcessing(false);
        }
    };

    const formatCurrency = (val: number) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val || 0);
    const formatDate = (val: any) => {
        if (!val) return '-';
        const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
        return d.toLocaleDateString('tr-TR');
    };

    if (loading || !settlement) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const st = STATUS_LABELS[settlement.status] || STATUS_LABELS.draft;
    const isProfitShare = settlement.agreementType === 'profit_share';

    return (
        <div className="p-6 max-w-5xl">
            {/* Header */}
            <div className="mb-6">
                <button onClick={() => navigate('/admin/settlements')} className="text-blue-600 hover:underline text-sm mb-2 flex items-center gap-1">
                    <ArrowLeft size={14} /> Mutabakatlar
                </button>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{settlement.vendorName}</h1>
                        <p className="text-gray-500">{formatDate(settlement.periodStart)} - {formatDate(settlement.periodEnd)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`text-sm px-3 py-1 rounded-full font-medium ${st.color}`}>{st.label}</span>
                        <span className="text-xs px-2 py-1 rounded bg-gray-100">
                            {isProfitShare ? 'Kâr Ortaklığı' : 'Ciro Komisyonu'} — %{settlement.platformShareRate}
                        </span>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-xs text-gray-500">Toplam Ciro</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(settlement.totalRevenue)}</p>
                    <p className="text-xs text-gray-400">{settlement.orderCount} sipariş</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-xs text-gray-500">Toplam Giderler</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(settlement.totalExpenses)}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-xs text-gray-500">Platform Payı</p>
                    <p className="text-xl font-bold text-blue-600">{formatCurrency(settlement.platformShare)}</p>
                    <p className="text-xs text-gray-400">%{settlement.platformShareRate}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-xs text-gray-500">Vendor Payı</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(settlement.vendorShare)}</p>
                </div>
            </div>

            {/* Expense Breakdown */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="font-semibold text-lg mb-4">Gider Kırılımı</h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                        <p className="text-xs text-gray-500">Kurye Ücretleri</p>
                        <p className="font-semibold">{formatCurrency(settlement.totalCourierFees)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Malzeme Maliyeti</p>
                        <p className="font-semibold">{formatCurrency(settlement.totalMaterialCosts)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">POS Komisyonu</p>
                        <p className="font-semibold">{formatCurrency(settlement.totalPosCommission)}</p>
                    </div>
                    {isProfitShare && (
                        <div>
                            <p className="text-xs text-gray-500">Platform Giderleri</p>
                            <p className="font-semibold">{formatCurrency(settlement.platformExpenses)}</p>
                        </div>
                    )}
                    <div>
                        <p className="text-xs text-gray-500">Net Kâr</p>
                        <p className="font-semibold text-gray-900">{formatCurrency(settlement.netProfit)}</p>
                    </div>
                </div>
            </div>

            {/* Payment Status */}
            {(settlement.status === 'confirmed' || settlement.status === 'paid') && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="font-semibold text-lg mb-4">Ödeme Durumu</h2>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <p className="text-xs text-gray-500">Ödenen</p>
                            <p className="font-semibold text-green-600">{formatCurrency(settlement.paidAmount)}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Kalan</p>
                            <p className="font-semibold text-orange-600">{formatCurrency(settlement.remainingAmount)}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Ödeme Tarihi</p>
                            <p className="font-semibold">{settlement.paidAt ? formatDate(settlement.paidAt) : '-'}</p>
                        </div>
                    </div>
                    {settlement.paymentNote && (
                        <p className="text-sm text-gray-600 mt-3">Not: {settlement.paymentNote}</p>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
                {settlement.status === 'draft' && (
                    <>
                        <button
                            onClick={handleConfirm}
                            disabled={processing}
                            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                        >
                            <CheckCircle size={18} /> Onayla
                        </button>
                        <button
                            onClick={handleRecalculate}
                            disabled={processing}
                            className="flex items-center gap-2 border border-gray-300 px-5 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
                        >
                            <RefreshCw size={18} /> Yeniden Hesapla
                        </button>
                    </>
                )}
                {settlement.status === 'confirmed' && (
                    <button
                        onClick={() => setShowPayModal(true)}
                        disabled={processing}
                        className="flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                    >
                        <CreditCard size={18} /> Ödendi İşaretle
                    </button>
                )}
            </div>

            {/* Pay Modal */}
            {showPayModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowPayModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-lg font-bold mb-4">Ödeme Kaydet</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ödeme Tutarı (₺)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={payForm.paidAmount}
                                    onChange={(e) => setPayForm({ ...payForm, paidAmount: parseFloat(e.target.value) || 0 })}
                                    className="w-full border rounded-lg px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ödeme Yöntemi</label>
                                <select
                                    value={payForm.paymentMethod}
                                    onChange={(e) => setPayForm({ ...payForm, paymentMethod: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                >
                                    <option value="bank_transfer">Banka Havalesi/EFT</option>
                                    <option value="cash">Nakit</option>
                                    <option value="other">Diğer</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
                                <textarea
                                    value={payForm.paymentNote}
                                    onChange={(e) => setPayForm({ ...payForm, paymentNote: e.target.value })}
                                    rows={2}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="Opsiyonel ödeme notu..."
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handlePay}
                                disabled={processing}
                                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                            >
                                {processing ? 'Kaydediliyor...' : 'Ödemeyi Kaydet'}
                            </button>
                            <button
                                onClick={() => setShowPayModal(false)}
                                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition"
                            >
                                İptal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
